import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from "react-redux";
import {compose} from "redux";
import DataGrid, {SelectColumn} from 'react-data-grid';
import Spinner from "../spinner/Spinner";
import CustomSnackbar from "../alert/CustomSnackbar";
import {debounce} from "lodash";
import {GetSelectedRowID, IsAtBottom} from "../reporting-table/ReactDataGridUtils";
import {Box, Chip, Divider, IconButton, Tooltip, useTheme} from "@mui/material";
import 'react-data-grid/lib/styles.css';
import useCustomStyles from "../utils/UseCustomStyles";
import {automationRulesAction} from "../../store/actions/automationRulesAction";
import {styled} from "@mui/system";
import moment from "moment/moment";
import {useHistory, useLocation} from "react-router-dom";
import {alertActions} from "../../store/actions/alertActions";
import {FiberManualRecord, HistoryOutlined} from "@mui/icons-material";
import {AutomationStatusSwitchColumn} from "../reporting-table/Columns";
import AutomationToolbar from "./AutomationToolbar";
import {AutomationDeleteDialog} from "./AutomationDesign";
import ReportEmptyState from '../reporting-table/ReportEmptyState';
import {reportingGridStyles} from "../utils/ReportingToolbarDesign";
import {CustomDataGridBox} from "../dashboard/Dashboard";
import {automationLogsLocation} from './automationLogLinks';

const customStyles = (theme) => ({
    dataGrid: theme.clicksReportDataGrid
});

const AutomationRulesTableContainer = styled(Box)({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minHeight: 0,
    minWidth: 0
});

function AutomationRules(props) {

    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const history = useHistory();
    const location = useLocation();

    const [prompt, setPrompt] = useState(false);
    const [deleteRuleId, setDeleteRuleId] = useState(0);
    const [selectedRows, setSelectedRows] = useState(() => new Set());

    const {
        alert, getReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows,
        deleteRule, alertError, updateRuleStatus
    } = props;
    const {data, isLoading, errorMsg, domain, orderBy, sortBy, sortColumns} = props?.automationRulesState;
    const {plan} = props?.auth?.subscription;

    const userWorkspaces = props?.workspace?.userWorkspaces || [];
    const workspacesReady = userWorkspaces.length > 0;
    const workspaceIDs = userWorkspaces.filter((workspace) => workspace?.selected).map((workspace) => workspace?.workspaceId).join(",");

    const openLogs = rule => {
        const target = automationLogsLocation(rule?.id, {background: location, ruleName: rule?.name});
        if (target) history.push(target);
    };

    const getColumns = () => {
        let cachedColumns = JSON.parse(localStorage.getItem("automation-rules-columns"));
        let columns = [
            SelectColumn,
            AutomationStatusSwitchColumn((props) => updateRuleStatus(props)),
            {
                key: 'actionLogs', name: 'Logs', width: 65, resizable: true, sortable: false, draggable: true, frozen: false,
                renderCell: ({row}) => <Tooltip title="View action logs">
                    <IconButton size="small" aria-label={`View logs for ${row.name}`} onClick={event => {
                        event.stopPropagation();
                        openLogs(row);
                    }}><HistoryOutlined fontSize="small"/></IconButton>
                </Tooltip>
            },
            {
                key: 'status',
                name: 'Status',
                hidden: false,
                width: 85,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    let color = "inherit";
                    let deliveryStatus = "";
                    if (row?.status === "active") {
                        color = "primary";
                        deliveryStatus = "Active";
                    } else if (row?.status === "paused") {
                        color = "warning";
                        deliveryStatus = "Paused";
                    } else if (row?.status === "disabled") {
                        color = "disabled"
                        deliveryStatus = "Disabled";
                    }
                    return <>
                        <FiberManualRecord
                            fontSize={"small"} color={color}
                            style={{position: "relative", top: "2px", right: "3px", fontSize: "13px"}}
                        />
                        {deliveryStatus}
                    </>;
                }
            },
            {key: 'id', name: 'Rule ID', hidden: false, width: 70, frozen: false, sortable: true, resizable: true},
            {
                key: 'name',
                name: 'Name',
                hidden: false,
                width: 200,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'trafficSourceName',
                name: 'Traffic Source',
                hidden: false,
                width: 150,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'targetMode',
                name: 'Target Mode',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'entityType',
                name: 'Entity Type',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'ruleLogic',
                name: 'Rule Logic',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'lastExecutionStatus',
                name: 'Last Execution Status',
                hidden: false,
                width: 160,
                sortable: false,
                draggable: false,
                resizable: true,
                renderCell({row}) {
                    if (row?.lastExecutionStatus === "success") {
                        return <Chip label="Success" variant="outlined" color="primary" size="small"
                                     sx={{height: "24px", marginTop: 0}}/>
                    } else if (row?.lastExecutionStatus === "failed") {
                        return <Chip label="Failed" variant="outlined" color="error" size="small"
                                     sx={{height: "24px", marginTop: 0}}/>
                    } else if (row?.lastExecutionStatus === "partial") {
                        return <Chip label="Partial" variant="outlined" color="error" size="small"
                                     sx={{height: "24px", marginTop: 0}}/>
                    } else {
                        return <>-</>
                    }
                }
            },
            {
                key: 'nextRunAt',
                name: 'Next Run At',
                hidden: false,
                width: 178,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return row?.nextRunAt === null ? "-" : moment.utc(row?.nextRunAt).format("YYYY-MM-DD HH:mm") + " (UTC)"
                }
            },
            {
                key: 'lastRunAt',
                name: 'Last Run At',
                hidden: false,
                width: 178,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return row?.lastRunAt === null ? "-" : moment.utc(row?.lastRunAt).format("YYYY-MM-DD HH:mm") + " (UTC)"
                }
            },
            {
                key: 'createdAt',
                name: 'Created At',
                hidden: false,
                width: 178,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return row?.createdAt === null ? "-" : moment.utc(row?.createdAt).format("YYYY-MM-DD HH:mm") + " (UTC)"
                }
            },
            {
                key: 'workspaceName',
                name: 'Workspace',
                hidden: false,
                width: 105,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'executionCount',
                name: 'Execution Count',
                hidden: false,
                width: 130,
                sortable: false,
                draggable: false,
                resizable: true,
                renderCell({row}) {
                    return row?.executionCount
                }
            },
        ];

        if (cachedColumns !== null && cachedColumns?.length !== columns.length) {
            return columns;
        }

        if (cachedColumns !== null && cachedColumns !== "" && cachedColumns !== undefined) {
            let sortedColumns = [];
            cachedColumns?.forEach(cachedCol => {
                columns?.forEach(defCol => {
                    if (cachedCol?.key === defCol?.key) {
                        defCol.width = cachedCol.width;
                        sortedColumns.push(defCol);
                    }
                });
            });
            return sortedColumns;
        }
        localStorage.setItem("automation-rules-columns", JSON.stringify(columns));
        return columns;
    };

    const handleDeletePrompt = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                setDeleteRuleId(selectedRowId);
                setPrompt(true);
            }
        }
    };

    const handleDeleteAutomationRule = (prompt) => {
        if (!prompt) {
            setPrompt(false);
            return false;
        } else {
            deleteRule({ruleId: deleteRuleId});
            setPrompt(false);
        }
    };

    const gridRef = useRef(null);
    const lastScrollTop = useRef(0);

    const [columns, setColumns] = useState(getColumns());

    const onColumnsReorder = (sourceKey, targetKey) => {
        const sourceColumnIndex = columns.findIndex((c) => c.key === sourceKey);
        const targetColumnIndex = columns.findIndex((c) => c.key === targetKey);
        const reorderedColumns = [...columns];
        reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
        localStorage.setItem("automation-rules-columns", JSON.stringify(reorderedColumns));
        setColumns(reorderedColumns);
    };

    const handleInfinityScroll = (event) => {
        const {scrollTop} = event.currentTarget;
        const scrollingDown = scrollTop > lastScrollTop.current;
        lastScrollTop.current = scrollTop;

        if (!scrollingDown || isLoading || errorMsg || !workspacesReady || !IsAtBottom(event)) {
            return;
        }

        const hasMoreRows = data?.page > 0 && data?.recordsPerPage > 0 &&
            data.page < Math.ceil(data?.totalRecords / data.recordsPerPage);
        if (hasMoreRows) {
            loadMoreRows(domain, data.page + 1, orderBy, sortBy, workspaceIDs);
        }
    };

    const handleGetReport = () => {
        if (!workspacesReady || isLoading) {
            return;
        }
        lastScrollTop.current = 0;
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(domain, 1, orderBy, sortBy, workspaceIDs);
    };

    const cacheColumnResize = (idx, width) => {
        let cachedColumns = JSON.parse(localStorage.getItem("automation-rules-columns"));
        let modColumns = [];
        cachedColumns?.forEach((item, index) => {
            if (index === idx) {
                item.width = width;
            }
            modColumns.push(item);
        });
        localStorage.setItem("automation-rules-columns", JSON.stringify(modColumns));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(cacheColumnResize, 50), []);

    const handleClickOnAddRule = () => {

        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        history.push({
            pathname: "/automation/new",
            state: {background: location}
        });
    };

    const handleClickOnAddEdit = () => {

        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        let id = 0;
        if (selectedRows.size === 1) {
            id = GetSelectedRowID(selectedRows);
        }


        history.push({
            pathname: "/automation/edit/" + id,
            state: {background: location}
        });
    };

    useEffect(() => {
        if (!workspacesReady) {
            return;
        }

        lastScrollTop.current = 0;
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(domain, 1, orderBy, sortBy, workspaceIDs);

    }, [getReport, domain, sortBy, orderBy, workspaceIDs, workspacesReady]);

    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            }
        }

        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);

    }, [updateOrderBy, updateSortBy, sortColumns]);

    return (

        <Box display="flex" flexDirection="column" sx={{height: '100%', minHeight: 0}}>
            <AutomationToolbar
                selectedCount={selectedRows.size}
                onCreate={handleClickOnAddRule}
                onEdit={handleClickOnAddEdit}
                onDelete={handleDeletePrompt}
                onViewLogs={() => openLogs(data?.records?.find(rule => selectedRows.has(rule.id)))}
                onRefresh={handleGetReport}
                refreshing={isLoading || !workspacesReady}
            />
            <Divider/>
            <AutomationRulesTableContainer>
                {prompt &&
                    <AutomationDeleteDialog
                        ruleName={data?.records?.find(rule => rule.id === deleteRuleId)?.name}
                        confirm={handleDeleteAutomationRule}
                    />
                }

                {alert.errorMsg !== "" && <CustomSnackbar message={alert.errorMsg} timeout={8000}/>}
                {alert.successMsg !== "" &&
                    <CustomSnackbar message={alert.successMsg} severity="primary" timeout={8000}/>
                }

                <CustomDataGridBox sx={reportingGridStyles}>

                    {isLoading && <Spinner overComponentBox={true}/>}

                    {(columns !== null && classes !== null) &&
                        <DataGrid
                            ref={gridRef}
                            rowKeyGetter={(row) => row?.id}
                            columns={columns}
                            onColumnsReorder={onColumnsReorder}
                            rows={data?.records ? data.records : []}
                            defaultColumnOptions={{
                                sortable: true,
                                resizable: true,
                                draggable: true
                            }}
                            onColumnResize={(idx, width) => handleColumnResize(idx, width)}
                            sortColumns={sortColumns}
                            onSortColumnsChange={updateSortColumns}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={setSelectedRows}
                            onSelectedCellChange={({rowIdx, row, column}) => {
                                if (rowIdx !== -1 && !["select-row", "statusSwitch", "actionLogs"].includes(column?.key)) {
                                    setSelectedRows(new Set().add(row?.id));
                                }
                            }}
                            onScroll={handleInfinityScroll}
                            enableVirtualization={false}
                            className={classes?.dataGrid}
                        />
                    }
                {workspacesReady && !isLoading && !errorMsg && !alert.errorMsg && columns !== null && classes !== null &&
                    data?.page > 0 && !data?.records?.length && <ReportEmptyState kind="automation"/>}
                </CustomDataGridBox>
            </AutomationRulesTableContainer>
        </Box>
    );
}

const mapStateToProps = (state) => {
    return {
        automationRulesState: state.automationRules,
        auth: state.auth,
        alert: state.alert,
        workspace: state.workspace
    }
};

const actionCreators = {
    getReport: automationRulesAction.read,
    loadMoreRows: automationRulesAction.readMoreRows,
    updateOrderBy: automationRulesAction.updateOrderBy,
    updateSortBy: automationRulesAction.updateSortBy,
    updateRule: automationRulesAction.updateRule,
    updateRuleStatus: automationRulesAction.updateRuleStatus,
    updateSortColumns: automationRulesAction.updateSortColumns,
    deleteRule: automationRulesAction.deleteRule,
    alertError: alertActions.error,
};

export default compose(connect(mapStateToProps, actionCreators))(AutomationRules);
